import React, { useState } from 'react';
import { useContext } from 'react';
import DownloadCurveArrow from '../../../images/downloadcurvearrow.png';
import { BsZoomIn } from 'react-icons/bs';
import { Link } from 'react-router-dom';
import LazyImage from './LazyImage';
import { Card } from '../../micro/Card/Card';
import { PortContext } from '../../../service/MessageChannelContext';
import './skinHealth.scss';

function getScoreKey(score) {
  if (score < 4) {
    return 'canBeImproved';
  }
  if (score < 7) {
    return 'moderate';
  }
  if (score < 9) {
    return 'isGood';
  }
  return 'isExcellent';
}

const scoreTextMap = {
  skinHealth: {
    canBeImproved:
      "You've got this! It's okay to have skin challenges; you're not alone. Keep caring for yourself, and don't let it define you. Embrace your unique beauty, inside and out. Seek support, you're strong and amazing!",
    moderate:
      "You've got this! It's okay to have skin challenges; you're not alone. Keep caring for yourself, and don't let it define you. Embrace your unique beauty, inside and out. Seek support, you're strong and amazing!",
    isGood:
      "You're fantastic! Your healthy skin is a reflection of your great care. Embrace your natural beauty and keep shining. Remember, you're unique and worthy of love and admiration. Stay confident, you're truly remarkable!",
    isExcellent:
      "You're a superstar! Your excellent healthy skin is enviable and shows your dedication. Embrace your natural beauty and keep caring for yourself. Remember, you're extraordinary and deserving of all the love and admiration. Stay confident, you're shining bright and truly amazing!",
  },
  acne: {
    canBeImproved:
      "You're strong and beautiful, acne doesn't define you. Keep your head high, it will get better with time and care. Embrace your uniqueness, you're so much more than your skin. Remember, you're not alone, and there's support and solutions available. Stay positive, you've got this!",
    moderate:
      "You're doing great! Don't be disheartened; it's a common phase. Keep up your skincare routine and stay positive. Your confidence shines through, and acne doesn't define you. Seek support, it will improve with time and care. You're not alone in this journey!",
    isGood:
      "Your skin is beautiful and unique, a few spots don't change that. Keep caring for your skin, and remember, it's normal. Stay confident and know that you're not alone. Embrace your natural beauty, you're amazing just the way you are!",
    isExcellent:
      "You're absolutely glowing! A little acne is no big deal, and your beauty shines through. Keep up the fantastic work with your skincare routine. Embrace your unique self, you're remarkable inside and out. Remember, we all have imperfections, and you're beautiful just as you are!",
  },
  texture: {
    canBeImproved:
      "You're amazing! Smooth skin is on the way, stay committed to your skincare routine. Your dedication will pay off, keep going! Embrace your uniqueness; your worth is not defined by texture. Believe in yourself, you're capable of achieving anything!",
    moderate:
      "You're doing great! Stay positive, and remember, your worth is not defined by your skin texture. Keep caring for yourself, and don't forget, you're beautiful inside and out. Embrace your uniqueness, you're truly amazing!",
    isGood:
      "You're glowing! Your smooth skin is beautiful, and you deserve to feel confident. Embrace your natural beauty, and know you're amazing just the way you are. Keep shining bright, you're one of a kind!",
    isExcellent:
      "You're a star! Your very smooth skin is a gift, and you're truly beautiful. Embrace your uniqueness and radiate confidence. Remember, you're special and deserving of all the love and admiration. Keep shining, you're extraordinary!",
  },
  poreSize: {
    canBeImproved:
      "You're incredible! Visible pores are natural and don't define your beauty. Embrace your uniqueness and take care of your skin. Stay confident, you're amazing and worthy of love and respect. Keep shining, you're truly remarkable!",
    moderate:
      "You're incredible! Visible pores are natural and don't define your beauty. Embrace your uniqueness and take care of your skin. Stay confident, you're amazing and worthy of love and respect. Keep shining, you're truly remarkable!",
    isGood:
      "You're amazing! Your very slightly visible pores are a part of your beauty. Embrace your uniqueness and take care of your skin. Remember, you're special and deserving of all the love and appreciation. Stay confident, you're fabulous just the way you are!",
    isExcellent:
      "You're incredible! Your barely visible pores are enviable and a sign of great skin. Embrace your unique beauty and keep caring for yourself. Remember, you're truly remarkable, inside and out. Stay confident, you're shining bright!",
  },
  darkSpots: {
    canBeImproved:
      "You're incredible! Dark spots don't define you; your beauty shines through. Keep caring for yourself, and remember, you're so much more than your skin. Embrace your uniqueness, and know that you're deserving of love and acceptance. Stay confident, you're amazing just the way you are!",
    moderate:
      "You're incredible! Dark spots don't define you; your beauty shines through. Keep caring for yourself, and remember, you're so much more than your skin. Embrace your uniqueness, and know that you're deserving of love and acceptance. Stay confident, you're amazing just the way you are!",
    isGood:
      "You're shining bright! Minimal dark spots are a part of your uniqueness. Embrace your beauty, inside and out. Keep caring for yourself, and remember, you're remarkable and deserving of love. Stay confident, you're extraordinary!",
    isExcellent:
      "You're a star! Very minimal dark spots are a sign of your beautiful skin. Embrace your uniqueness and keep shining. Remember, you're special and worthy of love and appreciation. Stay confident, you're amazing just the way you are!",
  },
  brightness: {
    canBeImproved:
      "You're fabulous! A little dullness is temporary, and your radiance will return. Embrace your unique beauty and take care of yourself. Stay positive, you're shining bright, and your glow will come back. Remember, you're truly remarkable!",
    moderate:
      "You're incredible! Slightly dull skin is normal and temporary. Embrace your unique beauty and keep caring for yourself. Your radiance will return, and you'll shine bright. Stay confident, you're amazing just the way you are!",
    isGood:
      "You're glowing! Your bright skin is stunning, and you radiate beauty. Embrace your unique self and keep shining. Remember, you're special and deserving of all the love and admiration. Stay confident, you're truly remarkable!",
    isExcellent:
      "You're glowing! Your bright skin is stunning, and you radiate beauty. Embrace your unique self and keep shining. Remember, you're special and deserving of all the love and admiration. Stay confident, you're truly remarkable!",
  },
};

const categories = ['skinHealth', 'acne', 'texture', 'poreSize', 'darkSpots', 'brightness'];

const categoryDisplayMap = {
  skinHealth: 'Skin Health',
  acne: 'Acne',
  texture: 'Texture',
  poreSize: 'Pore Size',
  darkSpots: 'Dark Spots',
  brightness: 'Brightness',
};

function getScoreClassName(isSelected, score) {
  if (isSelected) {
    if (score < 4) {
      return 'rank-box fill danger';
    }
    if (score < 7) {
      return 'rank-box fill warning';
    }
    return 'rank-box fill success';
  }
  if (score < 4) {
    return 'rank-box danger-outline';
  }
  if (score < 7) {
    return 'rank-box warning-outline';
  }
  return 'rank-box success-outline';
}

const healthDisplayMap = {
  acne: 'Acne',
  texture: 'Texture',
  poreSize: 'Pore Size',
  darkSpots: 'Dark Spots',
  brightness: 'Brightness',
  skinHealth: 'Your overall skin health',
};

const CONTENT = {
  title: {
    en: 'SkinTech Analyzer',
    id: 'SkinTech Analyzer',
  },
  skinNeedTitle: {
    en: 'Your overall skin health',
    id: 'Yuk, cobain SkinTech Analyzer Emina!', //todo: get these translations
  },
  skinNeedContent: {
    en: `You're fantastic! Your healthy skin is a reflection of your great care. Embrace your natural beauty and keep shining. Remember, you're unique and worthy of love and admiration. Stay confident, you're truly remarkable!`,
    id: 'Cari tahu lebih lanjut tentang masalah kulit kayak jerawat, kulit kusam, dan bintik hitam serta gimana cara mengatasinya di sini! Dapatkan juga produk rekomendasi dari kami yang khusus dibuat untukmu.',
  },
};

const SkinOverallHealth = ({ analysisReport, selectedHealth, updateSelectedHealth }) => {
  const { lang } = useContext(PortContext);
  const [step, setStep] = useState(1);

  return (
    <div>
      <div className="container">
        <div className="row position-relative">
          <div className="col-md-4 col-sm-12" style={{ paddingLeft: 0, paddingRight: 0 }}>
            <div className="col-md-4 col-sm-12" style={{ paddingLeft: 0, paddingRight: 0 }}>
              <div className="card-skin">
                <div className={`card-title-skin title-left`}>{`Your Skin Report`}</div>
                <div className="ranking ps-md-3">
                  <div className="row-scroll">
                    {categories.map((category) => (
                      <>
                        <div className="col-md-4 col-12">
                          <div className={getScoreClassName(selectedHealth === category, analysisReport.scores[category])}>
                            <div onClick={() => updateSelectedHealth(category)}>
                              <span> {analysisReport.scores[category]}</span>/10
                            </div>
                            <h6>{categoryDisplayMap[category]}</h6>
                          </div>
                        </div>
                      </>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card-analysis-detail">
            <div className={`card-title-skin title-left}`}>{healthDisplayMap[selectedHealth]}</div>
            <div className="">
              <div className="description">
                <h4 className="default-text">
                  {' '}
                  Your score: <span className="default-text text-success"> {analysisReport.scores[selectedHealth]}/10* </span>
                </h4>
                <div className="detail">
                  <h6>
                    {' '}
                    <span className="text-danger"> * </span>The higher your score, the better your skin is faring on this skin parameter{' '}
                  </h6>
                </div>
                <p> {scoreTextMap[selectedHealth][getScoreKey(analysisReport.scores[selectedHealth])]}</p>
                <Link to="/productRecommendation">
                  <button className="btn btn-primary w-100-mobile">See Products Recommendations</button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkinOverallHealth;
